<template>
  <dynamic-object-form-structure
    :object-uuid="objectUUID"
    :object-layer="objectLayer"
    :loading-structure="loadingStructure"
    :loading-save="loadingSave"
    :permission-name="form.permission"
    :object-type-translation-key="form.name_translation"
    :tabs="visibleTabs"
    :active-tab="activeTab"
    :action-type="actionType"
    :saveIsDisabled="!required.filled"
    :map-element="$parent.$refs.formsContainer"
    :previous-pos-x="previousPosX"
    :previous-pos-y="previousPosY"
    :data-was-changed="dataWasChanged"
    :fields-still-need-filled="required.stillNeedFilled"
    @updateActiveTab="activeTab = $event"
    @changeViewMode="changeViewModeOfObject"
    @openDetails="openDetails"
    @refresh="refresh"
    @clearData="clearData"
    @save="saveObject"
    @close="closeForm"
    :object-data="objectData"
  >
    <template v-slot:formFields>
      <div
        v-show="visibleTabs[activeTab].type === 'form'"
        class="form tab-content"
      >
        <template v-for="field in visibleTabs[activeTab].fields">
          <input-field
            v-if="field.var_name === 'cb_material_id'"
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="objectData.cb_logical_type ? getValueInObject(field.var_name, field.input_type) : null"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            inputType="other"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :hide="field.hide"
            :fieldDisabled="field.disable || !objectData.cb_logical_type"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
          >
            <div>
              <treeselect
                :value="getValueInObject(field.var_name, field.input_type)"
                @input="setValueInObject(field.var_name, $event, field.input_type)"
                :options="filteredCableMaterialOptions"
                :load-options="loadMaterialOptions"
                :normalizer="materialOptionsNormalizer"
                :disabled="
                  readOnly ||
                    !userCanEditTabContent(visibleTabs[activeTab]) ||
                    field.disable ||
                    !objectData.cb_logical_type
                "
                class="form-input-select"
                disableFuzzyMatching
              />
            </div>
          </input-field>

          <input-field
            v-else-if="field.var_name === 'cb_logical_subtype'"
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="
              objectData.cb_logical_type === '2'
                ? getValueInObject(field.var_name, field.input_type)
                : null
            "
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            inputType="other"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :hide="field.hide"
            :fieldDisabled="
              field.disable ||
                !objectData.cb_logical_type ||
                objectData.cb_logical_type !== '2'
            "
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
          >
            <div>
              <treeselect
                :value="
                  objectData.cb_logical_type === '2'
                    ? getValueInObject(field.var_name, field.input_type)
                    : null
                "
                @input="
                  setValueInObject(field.var_name, $event, field.input_type)
                "
                :options="cableRoleOptions"
                :disabled="
                  readOnly ||
                    !userCanEditTabContent(visibleTabs[activeTab]) ||
                    field.disable ||
                    !objectData.cb_logical_type ||
                    objectData.cb_logical_type !== '2'
                "
                class="form-input-select"
                disableFuzzyMatching
              />
            </div>
          </input-field>

          <input-field
            v-else
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            @input="setValueInObject(field.var_name, $event, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            :inputType="field.input_type"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :selectOptions="field.select_list_name"
            :radioOptions="field.radio_options"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
            :hide="field.hide"
            :automatic-label="field.has_automatic_label"
            :labelIsLocked="objectData.cb_label_locked"
            @toggle-label-lock="changeLabelMode"
            :user-can-unlock-label="!userHasFibercloudPermission(form.permission + '_manage_label')"
            @generateLabel="getAutomaticLabel(autoLabelRequestData)"
            :checkboxOptions="field.checkbox_options"
            :object-data="objectData"
            @updateCheckboxValue="updateCheckboxValue($event)"
            @removePhoto="addToPhotosToRemove($event)"
          />
        </template>
      </div>

      <div
        v-show="visibleTabs[activeTab].type === 'other'"
        class="form tab-content"
      >
        <cable-pathways-section :pathways-data="pathwaysList" />
      </div>

      <accessories-and-others-tab
        v-show="visibleTabs[activeTab].label === 'Accessories'"
        :accessories-selected="accessoriesSelected"
        :accessories-selected-by-material-id="accessoriesSelectedByMaterialId"
        relation-family-id-select="ca"
        family-id="ac"
        :disabled="readOnly"
      />
      <accessories-and-others-tab
        v-show="visibleTabs[activeTab].label === 'Others'"
        :accessories-selected="othersSelected"
        :accessories-selected-by-material-id="othersSelectedByMaterialId"
        relation-family-id-select="ca"
        family-id="ot"
        :disabled="readOnly"
      />
      <custom-fields-tab
        v-if="visibleTabs[activeTab].code === 'custom_fields'"
        :customFields="customFields.custom_fields"
        :objectData="objectData.custom_fields"
        :actionType="actionType"
        :disabled="readOnly"
        @updateValue="setValueInObject"
        :requiredFilled="required.filled"
      />
    </template>
  </dynamic-object-form-structure>
</template>

<script>
import { dynamicObjectFormsMixin } from "@/mixins/forms/dynamicObjectForms/dynamicObjectFormsMixin";
import DynamicObjectFormStructure from "../_Templates/DynamicObjectFormStructure";
import InputField from "../_Molecules/InputField";
import CablePathwaysSection from "../_SpecialTabs/CablePathwaysSection";
import { bus } from "@/main";
import AccessoriesAndOthersTab from "@/components/Home/Forms/_SpecialTabs/AccessoriesAndOthers/AccessoriesAndOthersTab";
import CustomFieldsTab from "@/components/Home/Forms/_SpecialTabs/CustomFields/CustomFieldsTab.vue";
import { listsMixin } from "@/mixins/listsMixin";
import { mapActions } from "vuex";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "CableForm",

  components: {
    AccessoriesAndOthersTab,
    CablePathwaysSection,
    InputField,
    DynamicObjectFormStructure,
    CustomFieldsTab
  },

  mixins: [dynamicObjectFormsMixin, listsMixin,checkPermissionsMixin],

  props: {
    lineStringWKT: {
      type: Array
    },
    cableData: {
      type: Object
    }
  },

  data() {
    return {
      pathwaysList: [],
      cableRoleOptions: [],
    };
  },

  computed: {
    autoLabelRequestData() {
      let request = {};
      let data = [];
      if (this.actionType === "create") {
        let cableWKTLength = this.lineStringWKT.length;
        data[0] = {
          layer: this.lineStringWKT[0].layerName,
          uuid: this.lineStringWKT[0].uuid
        };
        data[1] = {
          layer: this.lineStringWKT[cableWKTLength - 1].layerName,
          uuid: this.lineStringWKT[cableWKTLength - 1].uuid
        };
      } else {
        data[0] = {};
        data[0].uuid = this.dataReceived.cb_uuid;
      }
      request = {
        layer: this.objectLayer,
        mode: this.actionType,
        data: data
      };
      return request;
    },

    /**
     * atualize the material when the role is selected
     */
    filteredCableMaterialOptions() {
      if(this.materialOptions){
        return this.objectData.cb_logical_subtype
          ? this.materialOptions.filter(item => item.family_specific_fields.logical_subtypes.includes(this.objectData.cb_logical_subtype))
          : this.materialOptions
      }
    },

    // if cluster tool is open execute different request when saving cable data
    getClusterToolForm() {
      return this.$store.getters.getClusterToolForm;
    },
    // gets cluster data from component to send when creating cables
    getClusterData() {
      return this.$store.getters.getClusterToolData;
    },

    cableTypeIsDistribution() {
      return this.objectData["cb_logical_type"] === "2";
    },

    materialRequestQueryParameters() {
      let queryParameters = {
        workspace_uuid: this.$store.getters.workspace.uuid,
        material_family_id: "ca",
        network_level_id: this.objectData.cb_logical_type,
        append: "family_specific_fields"
      };
      return queryParameters;
    },
  },

  watch: {
    loadingStructure(newValue) {
      if (!newValue) {
        this.loadPathwaysList();
        if (this.actionType === "create") {
          this.setCableLength();
          this.objectData.cb_logical_type = this.cableData.tier;
        }
      }
    },

    cableTypeIsDistribution() {
      if (!this.loadingStructure) {
        this.updateRoleInputField();
      }
    },

    "objectData.cb_logical_type"() {
      if (this.objectData.cb_logical_type === "2") {
        this.loadCableRoleOptions();
      }
    },

    "objectData.cb_logical_subtype"(value) {
      let varName = "cb_material_id";
      if (
        this.materialOptions &&
        !this.materialOptions.find(
          material => material.id === this.objectData.cb_material_id
        )
      ) {
        this.options = this.materialOptions.filter(option => option.family_specific_fields.logical_subtypes.includes(value));
        this.updateDependentFieldValueInObject(varName, false);
      }
    },

    "objectData.cb_material_id"(newValue, oldValue) {
        this.updateMaterialDependentFields(this.objectData.cb_material_id);
    },

    materialOptions() {
      if (this.materialOptions && this.objectData.cb_material_id) {
        this.updateMaterialDependentFields(this.objectData.cb_material_id);
      }
      if (!!this.options) {
        if (this.dataToSend.logical_subtype !== null){
          this.options = this.options.filter(option => option.family_specific_fields.logical_subtypes.includes(this.dataToSend.logical_subtype));
        } else {
          this.options = this.options.filter(option => option.family_specific_fields.logical_subtypes.includes(this.dataReceived.cb_logical_subtype));
        }
        this.options.filter(item => item.family_specific_fields.logical_subtypes.includes(this.objectData.cb_logical_subtype));
      }
    },
  },


  methods: {
    ...mapActions(["CLUSTER_TOOL_CREATE"]),
    loadPathwaysList() {
      let pathwaysList = [];
      if (this.actionType === "create") {
        if (
          this.cableData &&
          this.cableData.pathways &&
          this.cableData.pathways.length > 0
        ) {
          for (const pathway of this.cableData.pathways) {
            pathwaysList.push(pathway);
          }
        }
      } else {
        if (this.dataReceived && this.dataReceived.children) {
          this.dataReceived.children.forEach(child => {
            child.conducts.forEach(conduct => {
              conduct.pathways.forEach(pathway => {
                pathwaysList.push(pathway);
              });
            });
          });
        }
      }
      this.pathwaysList = pathwaysList;
    },

    setCableLength() {
      let length = 0;
      if (this.pathwaysList) {
        this.pathwaysList.forEach(pathway => {
          length = length + parseFloat(pathway.pw_length);
        });
      }
      this.objectData.cb_measured_length = length;
    },

    async createObject() {
      this.createDataToSend();
      if (this.getClusterToolForm) {
        const closuresArray = [];
        this.getClusterData.closures.forEach(item =>{
          if(!(closuresArray.some(element => element.nodes[1].obj.cl_id === item.nodes[1].obj.cl_id))){
            closuresArray.push(item);
          }
        })
        this.dataToSend.closures = closuresArray;
        this.dataToSend.initial_closure = this.getClusterData.initial_closure;
        this.dataToSend.end_closure = this.getClusterData.end_closure;
        this.dataToSend.projection = 4326;
        await this.saveClusterTool(this.dataToSend);
        this.$store.commit("SET_CLUSTER_TOOL_FORM", false);
      } else {
        this.BEST_ROUTE_FINAL(this.dataToSend)
          .then(() => {
            bus.$emit("end-cable-creation");

            this.finishObjectCreation();
          })
          .catch(error => {
            this.$toasted.global.toast_error(error.response.data.data);
            this.loadingSave = false;
          });
      }
    },
    // creates cables between closures
    async saveClusterTool(data) {
      try {
        await this.CLUSTER_TOOL_CREATE(data);
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingSave = false;
        this.finishObjectCreation();
      }
    },

    createDataToSend() {
      this.dataToSend = {
        node: this.lineStringWKT,
        projection: 3857,
        logical_type: this.objectData.cb_logical_type,
        status: this.objectData.cb_status,
        progress: this.objectData.cb_progress,
        measured_length: this.objectData.cb_measured_length,
        filterPathwaysWithIgnore: this.cableData.filterPathwaysWithIgnore
      };
      if (this.accessoriesSelected || this.othersSelected) {
        this.dataToSend.accessories = [];
        this.generateAccessoriesDataSend(this.accessoriesSelected);
        this.generateAccessoriesDataSend(this.othersSelected);
      }
      this.placeObjectDataInDataToSend();
    },

    updateMaterialDependentFields(materialID) {
      this.accessoriesSelectedByMaterialId = [];
      this.othersSelectedByMaterialId = [];
      if (materialID && this.materialOptions) {
        let materialOptions = this.materialOptions.find(
          material => material.sm_id === materialID
        );
        if (materialOptions) {
          this.getStockMaterial(materialOptions)
            .then(material => {
              if (material.others) {
                this.generateAccessoriesAndOthersSelected(
                  this.accessoriesSelectedByMaterialId,
                  this.othersSelectedByMaterialId,
                  material.others
                );
              }
              if (material.family_specific_fields) {
                this.setValueInObject(
                  "cb_capacity",
                  material.family_specific_fields.smc_capacity
                );
                this.setValueInObject(
                  "cb_module",
                  material.family_specific_fields.smc_module
                );
                this.setValueInObject(
                  "cb_helix_factor",
                  material.family_specific_fields.smc_helix
                );
                this.setValueInObject(
                  "cb_sag_factor",
                  material.family_specific_fields.smc_sag_factor
                );
              }
            })
            .catch(error => {
              console.error(error);
            });
        }
      } else {
        this.setValueInObject("cb_capacity", null);
        this.setValueInObject("cb_module", null);
        this.setValueInObject("cb_helix_factor", null);
        this.setValueInObject("cb_sag_factor", null);
      }
    },

    updateRoleInputField() {
      let fields = this.form.tabs[0].fields;
      this.updateDependentFieldDisableValue(
        "cb_logical_subtype",
        this.cableTypeIsDistribution,
        fields
      );
    },

    async loadCableRoleOptions() {
      let previousCableRole = window.sessionStorage.getItem("lastCable")
        ? JSON.parse(window.sessionStorage.getItem("lastCable")).cb_logical_type
        : null;
      try {
        let roleOptions = await this.getList(
          "list_cb_logical_subtype",
          previousCableRole,
          false
        );
        roleOptions.forEach(option => {
          this.cableRoleOptions.push({
            id: option.code,
            label: option.libelle
          });
        });
      } catch (e) {
        console.error(e);
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/ObjectForms/CableForm.vue